import { useEffect, useState, useMemo } from "react";

import Clients from "./pages/Clients";
import Projects from "./pages/Projects";
import Products from "./pages/Products";
import Employees from "./pages/Employees";
import useAdminClientStore from "./states/useAdminClientStore";
import useAdminProductStore from "./states/useAdminProductStore";
import useAdminEmployeeStore from "./states/useAdminEmployeeStore";

import TimeClock from "./pages/TimeClock";

import { NavBar, PageHeader, PageHeaderProvider, AppSwitcher } from "@iq/models";
import { FiUsers, FiBriefcase, FiPackage, FiUserCheck, FiClock } from "react-icons/fi";
import { GoHome } from "react-icons/go";
import AppUserMenu from "../components/AppUserMenu";
import HeaderClockAction from "../components/HeaderClockAction";
import useAuthStore from "../states/authStore";

import CRMLogo from "../assets/icons/CRM_logo.svg";
import TicketsLogo from "../assets/icons/Tickets_logo.svg";
import AdminLogo from "../assets/icons/Admin_logo.svg";
const adminNavItems = [
    { id: 1, label: "Clients", icon: <FiUsers size={24} /> },
    { id: 2, label: "Projects", icon: <FiBriefcase size={24} /> },
    { id: 3, label: "Products", icon: <FiPackage size={24} /> },
    { id: 4, label: "Employees", icon: <FiUserCheck size={24} /> },
	{ id: 5, label: "Time Clock", icon: <FiClock size={24} /> },
];

const ADMIN_PAGE_HEADER_TITLES = {
    Clients: "Clients",
    Projects: "Projects",
    Products: "Products",
    Employees: "Employees",
    "Time Clock": "Time Clock",
};

function Admin_Main(){
    const appLinks = useMemo(() => ([
        { label: "CRM", image: CRMLogo, href: "/contacts", variant: "crm" },
        { label: "Tickets", image: TicketsLogo, href: "/IT", variant: "tickets" },
        { label: "Admin", image: AdminLogo, href: "/admin", variant: "admin" },
        { label: "Projects", image: AdminLogo, href: "/projects", variant: "projects" },
        { label: "Payment", image: AdminLogo, href: "/payment", variant: "payment" },
    ]), []);

    const [activePage, setActivePage] = useState("Clients");
    const profile = useAuthStore((state) => state.profile);
    const profileName = profile?.name ?? "User";
    const profileId = profile?.id ?? null;

    const clients = useAdminClientStore((state) => state.clients);
    const clientsHydrated = useAdminClientStore((state) => state.hydrated);
    const clientsLoading = useAdminClientStore((state) => state.loading);
    const clientsError = useAdminClientStore((state) => state.error);
    const hydrateClients = useAdminClientStore((state) => state.hydrateClients);
    const resetClients = useAdminClientStore((state) => state.resetClients);

    const products = useAdminProductStore((state) => state.products);
    const productsHydrated = useAdminProductStore((state) => state.hydrated);
    const productsLoading = useAdminProductStore((state) => state.loading);
    const productsError = useAdminProductStore((state) => state.error);
    const hydrateProducts = useAdminProductStore((state) => state.hydrateProducts);
    const resetProducts = useAdminProductStore((state) => state.resetProducts);
    const [pageHeader, setPageHeader] = useState({
        title: ADMIN_PAGE_HEADER_TITLES.Clients,
        back: false,
        onBack: null,
        children: null,
    });

    useEffect(() => {
        setPageHeader({
            title: ADMIN_PAGE_HEADER_TITLES[activePage] || ADMIN_PAGE_HEADER_TITLES.Clients,
            back: false,
            onBack: null,
            children: null,
        });
    }, [activePage]);

    const employees = useAdminEmployeeStore((state) => state.employees);
    const employeesHydrated = useAdminEmployeeStore((state) => state.hydrated);
    const employeesLoading = useAdminEmployeeStore((state) => state.loading);
    const employeesError = useAdminEmployeeStore((state) => state.error);
    const hydrateEmployees = useAdminEmployeeStore((state) => state.hydrateEmployees);
    const resetEmployees = useAdminEmployeeStore((state) => state.resetEmployees);

    useEffect(() => {
        if (activePage !== "Clients") { return; }
        if (!clientsHydrated && !clientsLoading && !clientsError) { hydrateClients(); }

    }, [activePage, clientsHydrated, clientsLoading, clientsError, hydrateClients]);

    useEffect(() => {
        if (activePage !== "Products") { return; }
        if (!productsHydrated && !productsLoading && !productsError) { hydrateProducts(); }

    }, [activePage, productsHydrated, productsLoading, productsError, hydrateProducts]);

    useEffect(() => {
        if (activePage !== "Employees") { return; }
        if (!employeesHydrated && !employeesLoading && !employeesError) { hydrateEmployees(); }

    }, [activePage, employeesHydrated, employeesLoading, employeesError, hydrateEmployees]);

    async function handleClientInserted() {
        resetClients();
        await hydrateClients();
    }

    async function handleProductInserted() {
        resetProducts();
        await hydrateProducts();
    }

    async function handleEmployeeInserted() {
        resetEmployees();
        await hydrateEmployees();
    }

    const renderPage = () => {
        switch(activePage) {
            case "Clients":
                return (
                    <Clients
                        clients={clients}
                        clientsLoading={clientsLoading}
                        clientsError={clientsError}
                        onClientInserted={handleClientInserted}
                    />
                );
            case "Projects":
                return <Projects />;
            case "Products":
                return (
                    <Products
                        products={products}
                        productsLoading={productsLoading}
                        productsError={productsError}
                        onProductInserted={handleProductInserted}
                    />
                );
            case "Employees":
                case "Employees":
                    return (
                        <Employees
                            employees={employees}
                            employeesLoading={employeesLoading}
                            employeesError={employeesError}
                            onEmployeeInserted={handleEmployeeInserted}
                        />
                    );
			case "Time Clock":
				return <TimeClock />;
            default:
                return <Clients />;
        }
    };

    return(
		<div className="crm-layout app">
			<div className="nav-section">
				<NavBar
					activePage={activePage}
					navItems={adminNavItems}
					onPageChange={setActivePage}
					footerAction={{
						label: "Home",
                        icon: <GoHome size={24} />,
						onClick: () => {
							window.location.href = "/login";
						},
					}}
				/>
			</div>
            <div className="page-logo-container">
                <img className="nav-logo" src={AdminLogo} alt="Admin Logo" />
            </div>
            <PageHeaderProvider userMenu={(
                <>
                    <HeaderClockAction />
                    <AppSwitcher apps={appLinks} activeLabel="Home" />
                    <AppUserMenu name={profileName} id={profileId} />
                </>
            )}
            >
                <PageHeader title={pageHeader.title} back={pageHeader.back} onBack={pageHeader.onBack}>
                    {pageHeader.children}
                </PageHeader>
				{renderPage()}
			</PageHeaderProvider>
		</div>
    )

}

export default Admin_Main